import {
  Elements,
  PaymentMethodMessagingElement,
} from '@stripe/react-stripe-js';
import { useMemo, useState } from 'react';
import { Pressable, View } from 'react-native';
import { withUnistyles } from 'react-native-unistyles';

import { getStripePromise } from '@/utils/stripeLoader';

import { styles } from './styles';
import type { BnplInstallmentMessageProps } from './types';
import { useMessagingConfig } from './useMessagingConfig';
import { useMessagingReadyWatchdog } from './useMessagingReadyWatchdog';

const StyledElements = withUnistyles(Elements);

const BnplInstallmentMessage = ({
  amountCents,
  currency,
  country,
  eligibleMethods,
  selected,
  onReady,
}: BnplInstallmentMessageProps) => {
  const [stripePromise] = useState(getStripePromise);
  const config = useMessagingConfig({
    amountCents,
    currency,
    country,
    eligibleMethods,
  });

  // Stripe.js names the country `countryCode`; keep that in the log so the
  // web and native warnings can be told apart.
  const options = useMemo(
    () =>
      config && {
        amount: config.amount,
        currency: config.currency,
        countryCode: config.country,
        paymentMethodTypes: config.paymentMethodTypes,
      },
    [config],
  );

  const { reportReady } = useMessagingReadyWatchdog({
    enabled: options !== null,
    // Both the selected and unselected instances must render before the row
    // reveals the message, otherwise toggling selection would flash a gap.
    expectedSignals: 2,
    onReady,
    logContext: options ?? {},
  });

  if (!options) return null;

  return (
    <Pressable
      style={styles.container}
      onPress={(event) => event.stopPropagation()}
    >
      <View style={selected ? styles.hidden : undefined}>
        <StyledElements
          stripe={stripePromise}
          uniProps={(_colors, rt) => ({
            options: {
              appearance: {
                theme: rt.themeName === 'dark' ? 'night' : 'stripe',
              },
            },
          })}
        >
          <PaymentMethodMessagingElement
            options={options}
            onReady={() => reportReady('unselected')}
          />
        </StyledElements>
      </View>

      <View style={!selected ? styles.hidden : undefined}>
        <StyledElements
          stripe={stripePromise}
          uniProps={() => ({
            options: {
              appearance: {
                theme: 'night',
              },
            },
          })}
        >
          <PaymentMethodMessagingElement
            options={options}
            onReady={() => reportReady('selected')}
          />
        </StyledElements>
      </View>
    </Pressable>
  );
};

BnplInstallmentMessage.displayName = 'BnplInstallmentMessage';
export { BnplInstallmentMessage };
